import { useMemo, useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { downloadBlob } from '@/lib/download';
import { Copy as CopyIcon, RefreshCcw, ShieldCheck, TypeIcon } from 'lucide-react';
import { Type } from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'What is Lorem Ipsum?', a: 'A scrambled version of a 1st-century BC Latin text by Cicero, used since the 1500s as placeholder text in typography. Its rhythm mimics real English prose without the words meaning anything, so readers focus on layout rather than content.' },
    { q: 'Should I ever ship Lorem Ipsum to production?', a: 'No. Replace it with real content before you launch. Real copy has different length, rhythm, and line breaks, and it exposes design issues that lorem ipsum hides.' },
    { q: 'Does this generator run locally?', a: 'Yes — it’s 100% client-side. Nothing is uploaded, logged, or stored.' },
    { q: 'Can I start with “Lorem ipsum dolor sit amet…”?', a: 'Yes. Toggle “Start with the classic opening” and the first paragraph will begin with the traditional Cicero fragment.' },
];

const HOW = [
    'Pick the unit you want — paragraphs, sentences, or words — and how many of them. The generator draws from a curated word bank of Latin-looking placeholder words and stitches them into sentences of natural length (8–16 words), and into paragraphs of 4–8 sentences.',
    'Optionally, start the very first paragraph with the classic opening line “Lorem ipsum dolor sit amet, consectetur adipiscing elit…” This is helpful when the placeholder text needs to feel familiar to designers reviewing a mockup.',
    'Everything is generated in your browser — no request is sent to our servers. Copy the result with the built-in Copy button, or download it as a .txt file. Regenerate as many times as you like; each pass gives a fresh mix.',
];
const BENEFITS = [
    'Placeholder text lets designers evaluate a layout without being distracted by what the words say. When you drop real copy into a mockup, reviewers instinctively read it and start critiquing the content. Lorem ipsum keeps everyone focused on typography, spacing, and hierarchy.',
    'Because generated Latin has a similar letter frequency and word length to English, it also gives an honest visual weight to your text blocks. You can see how a paragraph of your final copy will “sit” visually before it exists.',
    'And for stress-testing responsive designs, being able to generate quickly — 5, 20, 100 paragraphs — tells you if your grid, line-height, and column widths hold up as content grows.',
];
const TIPS = [
    'Use 3–5 paragraphs to test a typical article-length layout.',
    'Stress-test with 20+ paragraphs to catch layout bugs at scale.',
    'Replace lorem ipsum with real copy before internal reviews — stakeholders care about the words.',
];

const BANK = 'lorem ipsum dolor sit amet consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua enim ad minim veniam quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat duis aute irure reprehenderit in voluptate velit esse cillum eu fugiat nulla pariatur excepteur sint occaecat cupidatat non proident sunt culpa qui officia deserunt mollit anim id est laborum quisque velit fermentum vitae sagittis diam pellentesque bibendum posuere volutpat nulla facilisi vestibulum lacinia arcu eget nulla varius natoque penatibus magnis dis parturient montes nascetur ridiculus mus mauris rhoncus aenean vel elit scelerisque'.split(' ');

function rand(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min; }
function pick() { return BANK[rand(0, BANK.length - 1)]; }
function sentence() {
    const len = rand(8, 16);
    const parts = [];
    for (let i = 0; i < len; i++) parts.push(pick());
    const s = parts.join(' ');
    return s.charAt(0).toUpperCase() + s.slice(1) + '.';
}
function paragraph(sentences = null) {
    const count = sentences ?? rand(4, 8);
    const arr = [];
    for (let i = 0; i < count; i++) arr.push(sentence());
    return arr.join(' ');
}

export default function LoremIpsumPage() {
    const [unit, setUnit] = useState('paragraphs');
    const [count, setCount] = useState('5');
    const [classic, setClassic] = useState(true);
    const [output, setOutput] = useState('');
    const [seedTick, setSeedTick] = useState(0);

    const generate = () => {
        const n = Math.max(1, Math.min(500, Number(count) || 1));
        let text = '';
        if (unit === 'paragraphs') {
            const parts = [];
            for (let i = 0; i < n; i++) parts.push(paragraph());
            if (classic && parts.length > 0) {
                parts[0] = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ' + parts[0];
            }
            text = parts.join('\n\n');
        } else if (unit === 'sentences') {
            const parts = [];
            for (let i = 0; i < n; i++) parts.push(sentence());
            text = parts.join(' ');
            if (classic) text = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit. ' + text;
        } else {
            const parts = [];
            for (let i = 0; i < n; i++) parts.push(pick());
            text = parts.join(' ');
            text = text.charAt(0).toUpperCase() + text.slice(1) + '.';
        }
        setOutput(text);
        setSeedTick((t) => t + 1);
    };

    const wordCount = useMemo(() => (output.trim() ? output.trim().split(/\s+/).length : 0), [output]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(output);
            toast.success('Copied to clipboard.');
        } catch {
            toast.error('Could not access clipboard.');
        }
    };

    const download = () => {
        if (!output) return;
        const blob = new Blob([output], { type: 'text/plain' });
        downloadBlob(blob, 'lorem-ipsum.txt');
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Lorem Ipsum Generator',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Lorem Ipsum Generator"
                description="Generate placeholder text — paragraphs, sentences, or words — for mockups, wireframes, and print layouts. Runs in your browser."
                canonical="/tools/lorem-ipsum"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Lorem Ipsum Generator' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <Type className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Lorem Ipsum Generator</h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Generate placeholder text for mockups — paragraphs, sentences, or
                            just a run of words.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">In-browser</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Copy or download</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[0.55fr_1.45fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80 h-fit">
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="li-unit">Unit</Label>
                                <Select value={unit} onValueChange={setUnit}>
                                    <SelectTrigger id="li-unit" data-testid="tool-lorem-unit" className="mt-1.5">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="paragraphs">Paragraphs</SelectItem>
                                        <SelectItem value="sentences">Sentences</SelectItem>
                                        <SelectItem value="words">Words</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="li-count">How many</Label>
                                <Input
                                    id="li-count"
                                    data-testid="tool-lorem-count"
                                    type="number"
                                    min={1}
                                    max={500}
                                    value={count}
                                    onChange={(e) => setCount(e.target.value)}
                                    className="mt-1.5"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <Switch
                                    id="li-classic"
                                    data-testid="tool-lorem-classic"
                                    checked={classic}
                                    onCheckedChange={setClassic}
                                />
                                <Label htmlFor="li-classic" className="cursor-pointer">
                                    Start with the classic opening
                                </Label>
                            </div>
                            <Button
                                onClick={generate}
                                data-testid="tool-lorem-generate"
                                className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                            >
                                <RefreshCcw className="w-4 h-4 mr-2" /> Generate
                            </Button>
                        </div>
                    </Card>

                    <Card className="p-5 sm:p-6 border-border/80">
                        <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-slate-800">Output</p>
                            <p className="text-xs text-slate-500">{wordCount} words</p>
                        </div>
                        <Textarea
                            data-testid="tool-lorem-output"
                            value={output}
                            onChange={(e) => setOutput(e.target.value)}
                            rows={16}
                            placeholder="Your placeholder text will appear here after you press Generate."
                            className="mt-2 min-h-[320px] font-normal leading-relaxed"
                            key={seedTick}
                        />
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Button variant="secondary" onClick={copy} disabled={!output} data-testid="tool-lorem-copy">
                                <CopyIcon className="w-4 h-4 mr-2" /> Copy
                            </Button>
                            <Button variant="outline" onClick={download} disabled={!output} data-testid="tool-lorem-download">
                                Download .txt
                            </Button>
                        </div>
                    </Card>
                </div>

                <div className="mt-6 rounded-xl border border-border/70 bg-[hsl(var(--surface-2))] p-4">
                    <div className="flex items-center gap-2 text-slate-800">
                        <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                        <span className="text-sm font-semibold">Fully client-side</span>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">
                        Generation runs entirely in your browser. Nothing is uploaded.
                    </p>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
